import React from "react";
import {DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,} from "@/components/ui/dropdown-menu";
import {useI18next, useTranslation} from "gatsby-plugin-react-i18next";
import {navigate} from 'gatsby';
import {Globe, ChevronDown, Check} from "lucide-react";
import {setCookie} from "../../utils/siteInit";

// Shared with the admin app (app.chatlab.com) via the .chatlab.com cookie so the
// language chosen on the website carries over to the app and persists next visit.
const LANGUAGE_COOKIE_NAME = 'i18next';
// Separate cookie, written ONLY by an explicit pick in this switcher, and the only thing
// language detection is allowed to redirect on. `i18next` cannot be used for that: until
// 2026-09 it was also written passively on every page view, so visitors are carrying one
// that records an accidental landing rather than a choice.
const LANGUAGE_CHOICE_COOKIE = 'chatlab_lang';
const LANGUAGE_COOKIE_DAYS = 365;

// Display order: alphabetical by endonym, i.e. by the string the reader actually scans. Sorting by
// the English name would look random next to labels like Čeština, Deutsch and Magyar. A new language
// is INSERTED in the right place here, never appended - the list stopped matching the admin panel
// once both were kept in the order languages happened to be added.
// With three alphabets in the list, "alphabetical" means: Latin first, sorted with the accents
// folded so Čeština sits at C and Türkçe at T, then Greek, then Cyrillic. Collating them into one
// sequence would put Ελληνικά and Български in places no reader of either would look.
const LANGUAGES = [
    {code: "cs", name: "Čeština"},
    {code: "da", name: "Dansk"},
    {code: "de", name: "Deutsch"},
    {code: "et", name: "Eesti"},
    {code: "en", name: "English"},
    {code: "es", name: "Español"},
    {code: "fr", name: "Français"},
    {code: "it", name: "Italiano"},
    {code: "lv", name: "Latviešu"},
    {code: "lt", name: "Lietuvių"},
    {code: "hu", name: "Magyar"},
    {code: "nl", name: "Nederlands"},
    {code: "no", name: "Norsk"},
    {code: "pl", name: "Polski"},
    {code: "pt", name: "Português"},
    {code: "ro", name: "Română"},
    {code: "sk", name: "Slovenčina"},
    {code: "sr", name: "Srpski"},
    {code: "fi", name: "Suomi"},
    {code: "sv", name: "Svenska"},
    {code: "tr", name: "Türkçe"},
    {code: "el", name: "Ελληνικά"},
    {code: "bg", name: "Български"},
    {code: "ru", name: "Русский"},
    {code: "uk", name: "Українська"},
];


// Two columns filled top-to-bottom, not left-to-right. The list is alphabetical and a reader looks
// for their own language by running down one column - a row-major grid would scatter the alphabet
// across the gap. Twenty-five entries in a single column ran off the bottom of the viewport. Below
// the sm breakpoint the grid collapses to one column and the two halves simply stack, which keeps
// the alphabet unbroken.
const splitIntoColumns = (items) => {
    const half = Math.ceil(items.length / 2);
    return [items.slice(0, half), items.slice(half)];
};


const LanguageSwitcher = ({ mobile = false }) => {
    const {t} = useTranslation();
    const {languages, language, originalPath} = useI18next();

    // The cookie is written ONLY when the visitor picks a language from this switcher
    // (see the onClick handlers below). It must never record the language of a page the
    // visitor merely landed on: that turned an accidental first landing into a sticky
    // preference which then redirected every later /pl/, /it/ … URL back to it.

    function getLanguageForFullResources() {
        return language;
    }

    return (
        <DropdownMenu className="flex items-center justify-center gap-5">
            <DropdownMenuTrigger className="flex items-center gap-1.5 text-[var(--cl-muted)] hover:text-[var(--cl-ink)] border-0 transition-all cursor-pointer">
                <Globe className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                <span className="text-sm font-semibold uppercase">{language}</span>
                <ChevronDown className="w-3.5 h-3.5 opacity-70" strokeWidth={2.5} aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="min-w-[13rem] sm:min-w-[26rem] max-w-[calc(100vw-1.5rem)] rounded-[var(--cl-radius)] border border-[var(--cl-rule)] bg-[var(--cl-surface)] backdrop-blur-none p-1 shadow-[0_14px_36px_rgba(17,17,20,0.12)]"
            >
                <div className="grid grid-cols-1 gap-x-1 sm:grid-cols-2">
                    {splitIntoColumns(LANGUAGES.filter(({code}) => languages.includes(code))).map((column, columnIndex) => (
                    <ul className="flex flex-col" key={columnIndex}>
                    {column.map(({code: lang, name}) => {
                        const active = getLanguageForFullResources() === lang;
                        // A real href, not "#": this dropdown is the site's primary set of links
                        // to the other language versions, and a crawler only ever sees the markup.
                        const pathWithoutLang = originalPath || '/';
                        const href = lang === "en" ? pathWithoutLang : `/${lang}${pathWithoutLang}`;
                        return (
                            <li key={lang}>
                                <a
                                    href={href}
                                    aria-current={active ? "true" : undefined}
                                    className={`flex items-center justify-between gap-4 h-9 px-3 rounded-[var(--cl-radius)] transition-colors ${
                                        active
                                            ? "bg-[var(--cl-surface-2)] text-[var(--cl-ink)] font-semibold"
                                            : "text-[var(--cl-ink-2)] hover:bg-[var(--cl-surface-2)] hover:text-[var(--cl-ink)]"
                                    }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setCookie(LANGUAGE_CHOICE_COOKIE, lang, LANGUAGE_COOKIE_DAYS);
                                        setCookie(LANGUAGE_COOKIE_NAME, lang, LANGUAGE_COOKIE_DAYS);
                                        window.location.href = href;
                                    }}
                                >
                                    <span className="text-[14px] leading-none">{name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="cl-num text-[11px] uppercase tracking-[0.12em] text-[var(--cl-muted)]">{lang}</span>
                                        <Check
                                            className={`w-3.5 h-3.5 ${active ? "opacity-100 text-[var(--cl-ink)]" : "opacity-0"}`}
                                            strokeWidth={3}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </a>
                            </li>
                        );
                    })}
                    </ul>
                    ))}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageSwitcher;
